import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { 
  LogIn, LogOut, Clock, Coffee, Users, FileText, Plus, 
  Trash2, Edit2, Download, ChevronDown, Calendar, User, X, Check, Save,
  KeyRound, ShieldCheck, UserCog, MessageCircle, Send, Phone, Eye
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context helper
const getToken = () => localStorage.getItem("token");
const setToken = (token) => localStorage.setItem("token", token);
const clearToken = () => localStorage.removeItem("token");

const authHeader = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
});

// ==================== LOGIN COMPONENT ====================
const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      setToken(response.data.access_token);
      onLogin(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="login-page">
      <div className="glass-card rounded-2xl p-8 w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center mb-8">
          <div className="logo-shine mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_ada31d07-9919-478d-a639-cef6d3d83657/artifacts/jztknf1i_itsec%20logo%20bl2.png" 
              alt="IT SEC Logo" 
              className="w-32 h-32 object-contain"
            />
          </div>
          <h1 className="text-3xl gold-text font-bold">IT SEC SPZOO</h1>
          <p className="text-gray-400 mt-2">Personnel Management System</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white placeholder-gray-500 focus:border-[#d4a84b] transition-colors"
              placeholder="Enter your email or username"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white placeholder-gray-500 focus:border-[#d4a84b] transition-colors"
              placeholder="Enter password"
              required
              data-testid="password-input"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full gold-button py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="login-submit"
          >
            <LogIn className="w-5 h-5" />
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== STAFF MANAGEMENT MODAL ====================
const StaffModal = ({ isOpen, onClose, staff, onSave }) => {
  const [form, setForm] = useState({
    name: "",
    position: "",
    email: "",
    phone: ""
  });

  useEffect(() => {
    if (staff) {
      setForm({
        name: staff.name || "",
        position: staff.position || "",
        email: staff.email || "",
        phone: staff.phone || ""
      });
    } else {
      setForm({ name: "", position: "", email: "", phone: "" });
    }
  }, [staff, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form, staff?.id);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="staff-modal">
      <div className="glass-card rounded-2xl p-6 w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl gold-text font-bold">
            {staff ? "Edit Staff" : "Add New Staff"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="w-full px-4 py-3 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white"
              required
              data-testid="staff-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Position</label>
            <input
              type="text"
              value={form.position}
              onChange={(e) => setForm({...form, position: e.target.value})}
              className="w-full px-4 py-3 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white"
              data-testid="staff-position-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full px-4 py-3 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white"
              data-testid="staff-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({...form, phone: e.target.value})}
              className="w-full px-4 py-3 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white"
              data-testid="staff-phone-input"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 gold-button py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              data-testid="staff-save-btn"
            >
              <Check className="w-5 h-5" />
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== ATTENDANCE EDIT MODAL ====================
const AttendanceEditModal = ({ isOpen, onClose, record, onSave }) => {
  const [form, setForm] = useState({
    checkinDate: "",
    checkinTime: "",
    checkoutDate: "",
    checkoutTime: ""
  });

  useEffect(() => {
    if (record) {
      // Extract wall-clock date + time in Europe/Warsaw, independent of the browser's timezone.
      const parseInWarsaw = (isoStr) => {
        if (!isoStr) return { date: "", time: "" };
        const d = new Date(isoStr);
        // en-CA locale gives YYYY-MM-DD; en-GB gives HH:MM (24h).
        const date = d.toLocaleDateString("en-CA", { timeZone: "Europe/Warsaw" });
        const time = d.toLocaleTimeString("en-GB", { timeZone: "Europe/Warsaw", hour: "2-digit", minute: "2-digit", hour12: false });
        return { date, time };
      };
      
      const ci = parseInWarsaw(record.checkin);
      const co = parseInWarsaw(record.checkout);
      
      setForm({
        checkinDate: record.date || ci.date,
        checkinTime: ci.time,
        checkoutDate: co.date || (record.date || ""),
        checkoutTime: co.time
      });
    }
  }, [record, isOpen]);

  if (!isOpen || !record) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build an ISO 8601 string with an explicit Europe/Warsaw offset, so the backend receives
    // the correct wall-clock time regardless of the browser's timezone.
    const toWarsawISO = (dateStr, timeStr) => {
      if (!dateStr || !timeStr) return null;
      // Europe/Warsaw is CET (+01:00) in winter and CEST (+02:00) in summer (DST).
      // Detect the current offset for the target date by using Intl API.
      const probe = new Date(`${dateStr}T${timeStr}:00Z`);
      // Format that instant in Warsaw and read the offset via the "GMT+X" shortOffset.
      const fmt = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/Warsaw", timeZoneName: "shortOffset" });
      const parts = fmt.formatToParts(probe);
      const tzName = parts.find(p => p.type === "timeZoneName")?.value || "GMT+01:00";
      const m = tzName.match(/GMT([+-]\d{1,2})(?::?(\d{2}))?/);
      const hours = m ? parseInt(m[1], 10) : 1;
      const mins = m && m[2] ? parseInt(m[2], 10) : 0;
      const sign = hours >= 0 ? "+" : "-";
      const hh = String(Math.abs(hours)).padStart(2, "0");
      const mm = String(mins).padStart(2, "0");
      return `${dateStr}T${timeStr}:00${sign}${hh}:${mm}`;
    };
    
    const updateData = {
      date: form.checkinDate,
      checkin: toWarsawISO(form.checkinDate, form.checkinTime),
      checkout: form.checkoutTime ? toWarsawISO(form.checkoutDate, form.checkoutTime) : null,
      checkout_date: form.checkoutDate
    };
    
    onSave(record.id, updateData);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="attendance-edit-modal">
      <div className="glass-card rounded-2xl p-6 w-full max-w-lg animate-fade-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl gold-text font-bold">Edit Attendance Record</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-black/30 rounded-lg border border-[rgba(212,168,75,0.1)]">
          <p className="text-gray-400 text-sm">Staff Member</p>
          <p className="text-white font-medium">{record.staff_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Check-In Section */}
          <div className="p-4 bg-green-900/20 border border-green-500/20 rounded-lg">
            <h3 className="text-green-400 font-medium mb-3 flex items-center gap-2">
              <LogIn className="w-4 h-4" /> Check-In
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Date *</label>
                <input
                  type="date"
                  value={form.checkinDate}
                  onChange={(e) => setForm({...form, checkinDate: e.target.value})}
                  className="w-full px-3 py-2 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white text-sm"
                  required
                  data-testid="attendance-checkin-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Time *</label>
                <input
                  type="time"
                  value={form.checkinTime}
                  onChange={(e) => setForm({...form, checkinTime: e.target.value})}
                  className="w-full px-3 py-2 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white text-sm"
                  required
                  data-testid="attendance-checkin-time"
                />
              </div>
            </div>
          </div>

          {/* Check-Out Section */}
          <div className="p-4 bg-red-900/20 border border-red-500/20 rounded-lg">
            <h3 className="text-red-400 font-medium mb-3 flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Check-Out
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
                <input
                  type="date"
                  value={form.checkoutDate}
                  onChange={(e) => setForm({...form, checkoutDate: e.target.value})}
                  className="w-full px-3 py-2 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white text-sm"
                  data-testid="attendance-checkout-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Time</label>
                <input
                  type="time"
                  value={form.checkoutTime}
                  onChange={(e) => setForm({...form, checkoutTime: e.target.value})}
                  className="w-full px-3 py-2 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white text-sm"
                  data-testid="attendance-checkout-time"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Leave time empty if not checked out yet. Set date to next day for overnight shifts.</p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 gold-button py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              data-testid="attendance-save-btn"
            >
              <Save className="w-5 h-5" />
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== USER CREATE MODAL ====================
const UserCreateModal = ({ isOpen, onClose, onSave }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("viewer");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setUsername("");
      setPassword("");
      setRole("viewer");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setSubmitting(true);
    await onSave({ username: username.trim(), password, role });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in" data-testid="user-create-modal">
      <form
        onSubmit={handleSubmit}
        className="glass-card rounded-2xl p-8 w-full max-w-md mx-4 border border-[rgba(212,168,75,0.3)]"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl gold-text font-bold flex items-center gap-2">
            <UserCog className="w-6 h-6" />
            Add User
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              placeholder="e.g. john.doe"
              className="w-full bg-black/40 border border-[rgba(212,168,75,0.2)] rounded-lg px-3 py-2 text-white focus:border-amber-400 outline-none"
              data-testid="new-user-username"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-1 block">Password</label>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={3}
              placeholder="At least 3 characters"
              className="w-full bg-black/40 border border-[rgba(212,168,75,0.2)] rounded-lg px-3 py-2 text-white font-mono focus:border-amber-400 outline-none"
              data-testid="new-user-password"
            />
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Role</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setRole("admin")}
                className={`px-4 py-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                  role === "admin"
                    ? "bg-amber-500/20 border-amber-400 text-amber-400"
                    : "bg-black/30 border-gray-700 text-gray-400 hover:border-gray-500"
                }`}
                data-testid="role-admin-btn"
              >
                <ShieldCheck className="w-4 h-4" />
                Admin
              </button>
              <button
                type="button"
                onClick={() => setRole("viewer")}
                className={`px-4 py-3 rounded-lg border transition-all flex items-center justify-center gap-2 ${
                  role === "viewer"
                    ? "bg-cyan-500/20 border-cyan-400 text-cyan-400"
                    : "bg-black/30 border-gray-700 text-gray-400 hover:border-gray-500"
                }`}
                data-testid="role-viewer-btn"
              >
                <User className="w-4 h-4" />
                Viewer
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {role === "admin"
                ? "Admins can manage staff, attendance, and other users."
                : "Viewers have read-only access to attendance and reports."}
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-black/30"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 gold-button px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-user-btn"
          >
            <Save className="w-4 h-4" />
            {submitting ? "Saving..." : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ==================== PASSWORD CHANGE MODAL ====================
const PasswordChangeModal = ({ isOpen, onClose, user, onSave }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setConfirm("");
      setError("");
    }
  }, [isOpen]);

  if (!isOpen || !user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 3) {
      setError("Password must be at least 3 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    await onSave(user.id, password);
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in" data-testid="password-change-modal">
      <form
        onSubmit={handleSubmit}
        className="glass-card rounded-2xl p-8 w-full max-w-md mx-4 border border-[rgba(212,168,75,0.3)]"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl gold-text font-bold flex items-center gap-2">
            <KeyRound className="w-6 h-6" />
            Change Password
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          Setting a new password for{" "}
          <span className="text-amber-400 font-medium">{user.username}</span>
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">New Password</label>
            <input
              type="text"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              required
              minLength={3}
              autoFocus
              className="w-full bg-black/40 border border-[rgba(212,168,75,0.2)] rounded-lg px-3 py-2 text-white font-mono focus:border-amber-400 outline-none"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Confirm Password</label>
            <input
              type="text"
              value={confirm}
              onChange={(e) => { setConfirm(e.target.value); setError(""); }}
              required
              minLength={3}
              className="w-full bg-black/40 border border-[rgba(212,168,75,0.2)] rounded-lg px-3 py-2 text-white font-mono focus:border-amber-400 outline-none"
              data-testid="confirm-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-red-400" data-testid="password-error">{error}</p>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg border border-gray-700 text-gray-300 hover:bg-black/30"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 gold-button px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-password-btn"
          >
            <Save className="w-4 h-4" />
            {submitting ? "Saving..." : "Update Password"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ==================== MAIN DASHBOARD ====================
const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState("attendance");
  const [staffList, setStaffList] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState([]);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  
  // Attendance edit modal state
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  
  // Report filters
  const [filterDate, setFilterDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterStaff, setFilterStaff] = useState("");
  const [summaryPeriod, setSummaryPeriod] = useState("weekly");
  const [reportType, setReportType] = useState("attendance"); // attendance or breaks
  const [breakReports, setBreakReports] = useState([]);
  const [expandedBreaks, setExpandedBreaks] = useState({}); // Track which rows have expanded breaks

  // Users management state
  const [usersList, setUsersList] = useState([]);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // WhatsApp notification state
  const [waSettings, setWaSettings] = useState({ recipients: [], enabled: true, from_number: "", configured: false });
  const [waNewNumber, setWaNewNumber] = useState("");
  const [waPreview, setWaPreview] = useState({ morning: null, evening: null, night: null });
  const [waSending, setWaSending] = useState({});

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/staff`, authHeader());
      setStaffList(res.data);
    } catch (err) {
      console.error("Failed to fetch staff:", err);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, authHeader());
      setUsersList(res.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  const handleCreateUser = async (data) => {
    try {
      await axios.post(`${API}/users`, data, authHeader());
      setUserModalOpen(false);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create user");
    }
  };

  const handleChangePassword = async (userId, newPassword) => {
    try {
      await axios.put(`${API}/users/${userId}`, { password: newPassword }, authHeader());
      setPasswordModalOpen(false);
      setEditingUser(null);
      alert("Password updated successfully");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update password");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role: newRole }, authHeader());
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to change role");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, authHeader());
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const fetchWaSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/whatsapp`, authHeader());
      setWaSettings(res.data);
    } catch (err) {
      console.error("Failed to fetch whatsapp settings:", err);
    }
  }, []);

  const saveWaSettings = async (recipients, enabled) => {
    try {
      const res = await axios.put(`${API}/settings/whatsapp`,
        { recipients, enabled }, authHeader());
      setWaSettings({ ...waSettings, recipients: res.data.recipients, enabled: res.data.enabled });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleAddRecipient = async () => {
    const num = waNewNumber.trim();
    if (!num) return;
    const next = [...waSettings.recipients, num];
    await saveWaSettings(next, waSettings.enabled);
    setWaNewNumber("");
  };

  const handleRemoveRecipient = async (num) => {
    const next = waSettings.recipients.filter(r => r !== num);
    await saveWaSettings(next, waSettings.enabled);
  };

  const handleToggleEnabled = async () => {
    await saveWaSettings(waSettings.recipients, !waSettings.enabled);
  };

  const fetchPreview = async (shift) => {
    try {
      const res = await axios.get(`${API}/notifications/preview?shift=${shift}`, authHeader());
      setWaPreview(prev => ({ ...prev, [shift]: res.data }));
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to load preview");
    }
  };

  const handleSendNow = async (shift) => {
    if (!waSettings.recipients.length) {
      alert("Önce en az bir yönetici numarası ekleyin.");
      return;
    }
    if (!window.confirm(`${shift.toUpperCase()} vardiyası özetini şimdi göndermek istediğinize emin misiniz?`)) return;
    setWaSending(prev => ({ ...prev, [shift]: true }));
    try {
      const res = await axios.post(`${API}/notifications/send`, { shift }, authHeader());
      const okCount = (res.data.results || []).filter(r => r.ok).length;
      const failed = (res.data.results || []).filter(r => !r.ok);
      let msg = `Gönderildi: ${okCount}/${res.data.recipients.length}`;
      if (failed.length) {
        msg += `\nBaşarısız: ${failed.map(f => `${f.to} → ${f.info}`).join('\n')}`;
      }
      alert(msg);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to send");
    } finally {
      setWaSending(prev => ({ ...prev, [shift]: false }));
    }
  };

  const fetchAttendanceStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/attendance/status`, authHeader());
      setAttendanceStatus(res.data);
    } catch (err) {
      console.error("Failed to fetch attendance status:", err);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      let url = `${API}/reports/attendance?`;
      if (filterDate) url += `start_date=${filterDate}&`;
      if (filterEndDate) url += `end_date=${filterEndDate}&`;
      if (filterStaff) url += `staff_id=${filterStaff}`;
      
      const res = await axios.get(url, authHeader());
      setReports(res.data);
    } catch (err) {
      console.error("Failed to fetch reports:", err);
    }
  }, [filterDate, filterEndDate, filterStaff]);

  const fetchBreakReports = useCallback(async () => {
    try {
      let url = `${API}/reports/breaks?`;
      if (filterDate) url += `start_date=${filterDate}&`;
      if (filterEndDate) url += `end_date=${filterEndDate}&`;
      if (filterStaff) url += `staff_id=${filterStaff}`;
      
      const res = await axios.get(url, authHeader());
      setBreakReports(res.data);
    } catch (err) {
      console.error("Failed to fetch break reports:", err);
    }
  }, [filterDate, filterEndDate, filterStaff]);

  const fetchSummary = useCallback(async () => {
    try {
      let url = `${API}/reports/summary?period=${summaryPeriod}`;
      if (filterStaff) url += `&staff_id=${filterStaff}`;
      
      const res = await axios.get(url, authHeader());
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    }
  }, [summaryPeriod, filterStaff]);

  const seedData = async () => {
    // Skip if seeded recently (within last 24h) — saves an HTTP roundtrip on slow networks
    const last = localStorage.getItem('lastSeedAt');
    if (last && Date.now() - parseInt(last, 10) < 24 * 60 * 60 * 1000) {
      return;
    }
    try {
      await axios.post(`${API}/seed`);
      localStorage.setItem('lastSeedAt', String(Date.now()));
      fetchStaff();
      fetchAttendanceStatus();
    } catch (err) {
      console.error("Failed to seed data:", err);
    }
  };

  useEffect(() => {
    seedData();
  }, []);

  useEffect(() => {
    if (activeTab === "attendance") {
      fetchAttendanceStatus();
    } else if (activeTab === "staff") {
      fetchStaff();
    } else if (activeTab === "reports") {
      fetchReports();
      fetchBreakReports();
      fetchSummary();
    } else if (activeTab === "users") {
      fetchUsers();
      fetchWaSettings();
    }
  }, [activeTab, fetchAttendanceStatus, fetchStaff, fetchReports, fetchBreakReports, fetchSummary, fetchUsers, fetchWaSettings]);

  // Auto-refresh attendance status every 10 seconds for real-time duration updates
  // Pause when page is hidden (saves CPU/battery on iPad)
  useEffect(() => {
    if (activeTab !== "attendance") return;
    let interval = null;
    const start = () => {
      if (interval) return;
      interval = setInterval(() => fetchAttendanceStatus(), 10000);
    };
    const stop = () => {
      if (interval) { clearInterval(interval); interval = null; }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { fetchAttendanceStatus(); start(); }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeTab, fetchAttendanceStatus]);

  // Live sync via WebSocket — any check-in / break / check-out from another device refreshes our view instantly.
  useEffect(() => {
    if (activeTab !== "attendance") return;
    const wsBase = BACKEND_URL.replace(/^http/, 'ws');
    let ws = null;
    let reconnectTimer = null;
    let pingTimer = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      try {
        ws = new WebSocket(`${wsBase}/api/ws/attendance`);
      } catch (e) {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        // Send a ping every 25s to keep the connection alive (some proxies time-out idle WS)
        pingTimer = setInterval(() => {
          try { ws.send("ping"); } catch (e) { /* ignore */ }
        }, 25000);
      };
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "attendance_update") {
            fetchAttendanceStatus();
          }
        } catch (e) { /* ignore */ }
      };
      ws.onclose = () => {
        if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
        scheduleReconnect();
      };
      ws.onerror = () => {
        try { ws.close(); } catch (e) { /* ignore */ }
      };
    };

    const scheduleReconnect = () => {
      if (stopped) return;
      if (reconnectTimer) return;
      reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, 3000);
    };

    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (ws) { try { ws.close(); } catch (e) { /* ignore */ } }
    };
  }, [activeTab, fetchAttendanceStatus]);

  // Local timer for smooth second updates — only runs on the attendance tab and when visible
  const [localTime, setLocalTime] = useState(Date.now());
  useEffect(() => {
    if (activeTab !== "attendance") return;
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => setLocalTime(Date.now()), 1000);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeTab]);

  // Per-staff loading state — only the specific row's buttons are disabled instead of the whole table
  const [busyStaff, setBusyStaff] = useState({}); // { [staff_id]: 'checkin' | 'checkout' | 'break' | null }

  const handleCheckIn = async (staffId) => {
    if (busyStaff[staffId]) return;
    setBusyStaff(p => ({ ...p, [staffId]: 'checkin' }));
    try {
      await axios.post(`${API}/attendance/checkin`, { staff_id: staffId }, authHeader());
      await fetchAttendanceStatus();
    } catch (err) {
      alert(err.response?.data?.detail || "Check-in failed");
    } finally {
      setBusyStaff(p => ({ ...p, [staffId]: null }));
    }
  };

  const handleCheckOut = async (staffId) => {
    if (busyStaff[staffId]) return;
    // Safety confirm: on iPad Safari an accidental double-tap on Break could otherwise trigger this destructive action.
    const staff = attendanceStatus.find(s => s.staff_id === staffId);
    const staffName = staff?.staff_name || "personel";
    if (!window.confirm(`${staffName} için ÇIKIŞ yapılsın mı?`)) return;
    setBusyStaff(p => ({ ...p, [staffId]: 'checkout' }));
    try {
      await axios.post(`${API}/attendance/checkout`, { staff_id: staffId }, authHeader());
      await fetchAttendanceStatus();
    } catch (err) {
      alert(err.response?.data?.detail || "Check-out failed");
    } finally {
      setBusyStaff(p => ({ ...p, [staffId]: null }));
    }
  };

  const handleBreak = async (staffId) => {
    if (busyStaff[staffId]) return;
    setBusyStaff(p => ({ ...p, [staffId]: 'break' }));
    // Optimistic UI: flip on_break locally for instant feedback
    setAttendanceStatus(prev => prev.map(s =>
      s.staff_id === staffId ? { ...s, on_break: !s.on_break } : s
    ));
    try {
      await axios.post(`${API}/attendance/break`, { staff_id: staffId }, authHeader());
      await fetchAttendanceStatus();
    } catch (err) {
      // Revert optimistic flip
      setAttendanceStatus(prev => prev.map(s =>
        s.staff_id === staffId ? { ...s, on_break: !s.on_break } : s
      ));
      alert(err.response?.data?.detail || "Break toggle failed");
    } finally {
      setBusyStaff(p => ({ ...p, [staffId]: null }));
    }
  };

  const handleSaveStaff = async (form, staffId) => {
    try {
      if (staffId) {
        await axios.put(`${API}/staff/${staffId}`, form, authHeader());
      } else {
        await axios.post(`${API}/staff`, form, authHeader());
      }
      setStaffModalOpen(false);
      setEditingStaff(null);
      fetchStaff();
      fetchAttendanceStatus();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save staff");
    }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) return;
    try {
      await axios.delete(`${API}/staff/${staffId}`, authHeader());
      fetchStaff();
      fetchAttendanceStatus();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete staff");
    }
  };

  // Attendance record edit/delete handlers
  const handleEditAttendance = (record) => {
    setEditingAttendance(record);
    setAttendanceModalOpen(true);
  };

  const handleSaveAttendance = async (attendanceId, updateData) => {
    try {
      await axios.put(`${API}/attendance/${attendanceId}`, updateData, authHeader());
      setAttendanceModalOpen(false);
      setEditingAttendance(null);
      fetchReports();
      fetchSummary();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update attendance record");
    }
  };

  const handleDeleteAttendance = async (attendanceId) => {
    if (!window.confirm("Are you sure you want to delete this attendance record?")) return;
    try {
      await axios.delete(`${API}/attendance/${attendanceId}`, authHeader());
      fetchReports();
      fetchSummary();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete attendance record");
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Warsaw"
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pl-PL", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "Europe/Warsaw"
    });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "-";
    const d = new Date(isoString);
    const date = d.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "short",
      timeZone: "Europe/Warsaw"
    });
    const time = d.toLocaleTimeString("pl-PL", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Warsaw"
    });
    return `${date} - ${time}`;
  };

  const formatSecondsToTime = (seconds) => {
    if (!seconds || seconds <= 0) return "0h 0m 0s";
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hrs}h ${mins}m ${secs}s`;
  };

  // Calculate real-time duration based on checkin time
  const calculateLiveDuration = (checkinTime, breakSeconds = 0, onBreak = false) => {
    if (!checkinTime) return { work: 0, break: breakSeconds };
    const checkin = new Date(checkinTime);
    const now = new Date();
    const totalElapsed = Math.floor((now - checkin) / 1000);
    const workSeconds = Math.max(0, totalElapsed - breakSeconds);
    return { work: workSeconds, break: breakSeconds };
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "0:00";
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hrs}:${mins.toString().padStart(2, "0")}`;
  };

  const downloadCSV = () => {
    if (reports.length === 0) return;
    
    const headers = ["Staff Name", "Date", "Check In", "Check Out", "Total Break", "Total Work"];
    const rows = reports.map(r => [
      r.staff_name,
      r.date,
      formatTime(r.checkin),
      formatTime(r.checkout),
      formatDuration(r.total_break_minutes),
      formatDuration(r.total_work_minutes)
    ]);
    
    const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_report_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen" data-testid="dashboard">
      {/* Header */}
      <header className="glass-card border-b border-[rgba(212,168,75,0.2)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_ada31d07-9919-478d-a639-cef6d3d83657/artifacts/jztknf1i_itsec%20logo%20bl2.png" 
              alt="IT SEC Logo" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-xl gold-text font-bold">IT SEC SPZOO</h1>
              <p className="text-xs text-gray-400">Personnel Management</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-gray-300 text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                {user?.email}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${user?.role === 'admin' ? 'bg-amber-600/30 text-amber-400' : 'bg-blue-600/30 text-blue-400'}`}>
                {user?.role === 'admin' ? 'Admin' : 'Viewer'}
              </span>
            </div>
            <button
              onClick={onLogout}
              className="px-4 py-2 bg-red-900/50 hover:bg-red-800/50 border border-red-500/30 rounded-lg text-red-400 text-sm font-medium flex items-center gap-2 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2">
          {[
            { id: "attendance", label: "Attendance", icon: Clock },
            { id: "staff", label: "Staff Management", icon: Users },
            { id: "reports", label: "Reports", icon: FileText },
            ...(user?.role === 'admin' ? [{ id: "users", label: "Users", icon: UserCog }] : [])
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-all ${
                activeTab === tab.id 
                  ? "gold-button" 
                  : "bg-black/30 text-gray-400 hover:bg-black/50 hover:text-white border border-[rgba(212,168,75,0.1)]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 pb-8">
        {/* Attendance Tab */}
        {activeTab === "attendance" && (
          <div className="animate-fade-in" data-testid="attendance-panel">
            {/* Header with Stats */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <h2 className="text-3xl gold-text font-bold italic">Time Tracking</h2>
                <p className="text-gray-400 mt-1">Manage staff attendance, breaks and check-outs.</p>
              </div>
              <div className="flex gap-3">
                <div className="glass-card px-6 py-4 rounded-xl text-center min-w-[100px]">
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Total Staff</p>
                  <p className="text-3xl gold-text font-bold">{attendanceStatus.length}</p>
                </div>
                <div className="glass-card px-6 py-4 rounded-xl text-center min-w-[100px]">
                  <p className="text-xs text-gray-400 uppercase tracking-wider">Working</p>
                  <p className="text-3xl text-green-400 font-bold">{attendanceStatus.filter(s => s.is_checked_in && !s.on_break).length}</p>
                </div>
                <div className="glass-card px-6 py-4 rounded-xl text-center min-w-[100px]">
                  <p className="text-xs text-gray-400 uppercase tracking-wider">On Break</p>
                  <p className="text-3xl text-amber-400 font-bold">{attendanceStatus.filter(s => s.on_break).length}</p>
                </div>
              </div>
            </div>
            
            {/* Table */}
            <div className="glass-card rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-[rgba(212,168,75,0.1)] border-b border-[rgba(212,168,75,0.2)]">
                    <th className="py-4 px-6 text-left text-[#d4a84b] text-xs uppercase tracking-wider font-semibold">Staff Member</th>
                    <th className="py-4 px-6 text-center text-[#d4a84b] text-xs uppercase tracking-wider font-semibold">Status</th>
                    <th className="py-4 px-6 text-center text-[#d4a84b] text-xs uppercase tracking-wider font-semibold">Check-In Date & Time</th>
                    <th className="py-4 px-6 text-center text-[#d4a84b] text-xs uppercase tracking-wider font-semibold">Duration</th>
                    <th className="py-4 px-6 text-center text-[#d4a84b] text-xs uppercase tracking-wider font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceStatus.map((status, idx) => (
                    <tr 
                      key={status.staff_id} 
                      className={`animate-fade-in border-b border-[rgba(212,168,75,0.1)] ${idx % 2 === 0 ? 'bg-[rgba(212,168,75,0.03)]' : 'bg-transparent'}`}
                      style={{ animationDelay: `${idx * 0.05}s` }}
                    >
                      <td className="py-5 px-6">
                        <div>
                          <p className="text-white font-bold text-lg">{status.staff_name}</p>
                          <p className="text-[#d4a84b] text-xs uppercase">{status.position || 'PERSONEL'}</p>
                          {status.is_checked_in && (
                            <p className="text-gray-400 text-xs mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Total break today: <span className="text-amber-400">{formatSecondsToTime(calculateLiveDuration(status.checkin_time, status.current_break_seconds, status.on_break).break)}</span>
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-5 px-6 text-center">
                        {!status.is_checked_in ? (
                          <span className="px-4 py-2 bg-gray-800 text-gray-400 rounded-full text-sm inline-block">Not Checked In</span>
                        ) : status.on_break ? (
                          <span className="px-4 py-2 bg-amber-500 text-black rounded-full text-sm font-medium inline-flex items-center gap-1">
                            <span className="w-2 h-2 bg-black rounded-full animate-pulse"></span>
                            On Break
                          </span>
                        ) : (
                          <span className="px-4 py-2 bg-green-500 text-black rounded-full text-sm font-medium inline-flex items-center gap-1">
                            <span className="w-2 h-2 bg-black rounded-full animate-pulse"></span>
                            Working
                          </span>
                        )}
                      </td>
                      <td className="py-5 px-6 text-center">
                        {status.is_checked_in && status.checkin_time ? (
                          <span className="text-[#d4a84b]">
                            {formatDateTime(status.checkin_time)}
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="py-5 px-6 text-center">
                        {status.is_checked_in ? (
                          status.on_break ? (
                            <div className="text-gray-300">
                              <span className="text-amber-400 text-xs uppercase mr-1">break</span>
                              <span className="font-mono text-amber-400">{formatSecondsToTime(calculateLiveDuration(status.checkin_time, status.current_break_seconds).break)}</span>
                            </div>
                          ) : (
                            <div className="text-gray-300">
                              <span className="text-green-400 text-xs uppercase mr-1">work</span>
                              <span className="font-mono">{formatSecondsToTime(calculateLiveDuration(status.checkin_time, status.current_break_seconds).work)}</span>
                            </div>
                          )
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center justify-center gap-2">
                          {!status.is_checked_in ? (
                            <button
                              onClick={() => handleCheckIn(status.staff_id)}
                              disabled={!!busyStaff[status.staff_id]}
                              className="px-5 py-2.5 bg-green-600 hover:bg-green-500 active:bg-green-700 rounded-lg text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-wait min-w-[120px] justify-center"
                              data-testid={`checkin-${status.staff_id}`}
                            >
                              {busyStaff[status.staff_id] === 'checkin' ? (
                                <>
                                  <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <LogIn className="w-4 h-4" />
                                  Check In
                                </>
                              )}
                            </button>
                          ) : (
                            <div className="flex flex-col gap-2">
                              <button
                                onClick={() => handleBreak(status.staff_id)}
                                disabled={!!busyStaff[status.staff_id]}
                                className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 active:bg-amber-600 rounded-lg text-black text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-wait min-w-[120px] justify-center"
                                data-testid={`break-${status.staff_id}`}
                              >
                                {busyStaff[status.staff_id] === 'break' ? (
                                  <>
                                    <span className="inline-block w-4 h-4 border-2 border-black/40 border-t-black rounded-full animate-spin" />
                                    ...
                                  </>
                                ) : (
                                  <>
                                    <Coffee className="w-4 h-4" />
                                    {status.on_break ? "End Break" : "Break"}
                                  </>
                                )}
                              </button>
                              <button
                                onClick={() => handleCheckOut(status.staff_id)}
                                disabled={!!busyStaff[status.staff_id]}
                                className="px-5 py-2.5 bg-red-600 hover:bg-red-500 active:bg-red-700 rounded-lg text-white text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60 disabled:cursor-wait min-w-[120px] justify-center"
                                data-testid={`checkout-${status.staff_id}`}
                              >
                                {busyStaff[status.staff_id] === 'checkout' ? (
                                  <>
                                    <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                                    ...
                                  </>
                                ) : (
                                  <>
                                    <LogOut className="w-4 h-4" />
                                    Check Out
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {attendanceStatus.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-12 text-center text-gray-500">
                        No staff members found. Add staff in the Staff Management tab.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Staff Management Tab */}
        {activeTab === "staff" && (
          <div className="glass-card rounded-2xl p-6 animate-fade-in" data-testid="staff-panel">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl gold-text font-bold">Staff Management</h2>
              {user?.role === 'admin' && (
                <button
                  onClick={() => { setEditingStaff(null); setStaffModalOpen(true); }}
                  className="gold-button px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                  data-testid="add-staff-btn"
                >
                  <Plus className="w-5 h-5" />
                  Add Staff
                </button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full attendance-table">
                <thead>
                  <tr>
                    <th className="py-4 px-6 text-left text-[#d4a84b]">Name</th>
                    <th className="py-4 px-6 text-left text-[#d4a84b]">Position</th>
                    <th className="py-4 px-6 text-left text-[#d4a84b]">Email</th>
                    <th className="py-4 px-6 text-left text-[#d4a84b]">Phone</th>
                    {user?.role === 'admin' && <th className="py-4 px-6 text-center text-[#d4a84b]">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {staffList.map((staff, idx) => (
                    <tr key={staff.id} className="animate-fade-in" style={{ animationDelay: `${idx * 0.05}s` }}>
                      <td className="py-4 px-6 text-white font-medium">{staff.name}</td>
                      <td className="py-4 px-6 text-gray-300">{staff.position || "-"}</td>
                      <td className="py-4 px-6 text-gray-300">{staff.email || "-"}</td>
                      <td className="py-4 px-6 text-gray-300">{staff.phone || "-"}</td>
                      {user?.role === 'admin' && (
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => { setEditingStaff(staff); setStaffModalOpen(true); }}
                              className="p-2 bg-blue-600/30 hover:bg-blue-600/50 border border-blue-500/30 rounded-lg text-blue-400 transition-colors"
                              data-testid={`edit-staff-${staff.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteStaff(staff.id)}
                              className="p-2 bg-red-600/30 hover:bg-red-600/50 border border-red-500/30 rounded-lg text-red-400 transition-colors"
                              data-testid={`delete-staff-${staff.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {staffList.length === 0 && (
                    <tr>
                      <td colSpan={user?.role === 'admin' ? 5 : 4} className="py-12 text-center text-gray-500">
                        No staff members found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === "reports" && (
          <div className="space-y-6 animate-fade-in" data-testid="reports-panel">
            {/* Filters */}
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-lg gold-text font-bold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Report Filters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white"
                    data-testid="filter-start-date"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">End Date</label>
                  <input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    className="w-full px-4 py-2 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white"
                    data-testid="filter-end-date"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Staff Member</label>
                  <div className="relative">
                    <select
                      value={filterStaff}
                      onChange={(e) => setFilterStaff(e.target.value)}
                      className="w-full px-4 py-2 bg-black/50 border border-[rgba(212,168,75,0.2)] rounded-lg text-white appearance-none cursor-pointer"
                      data-testid="filter-staff"
                    >
                      <option value="">All Staff</option>
                      {staffList.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => { fetchReports(); fetchBreakReports(); fetchSummary(); }}
                    className="gold-button px-6 py-2 rounded-lg font-medium w-full"
                    data-testid="apply-filters-btn"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>

            {/* Report Type Filter */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg gold-text font-bold">Report Type</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReportType("attendance")}
                    className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      reportType === "attendance" 
                        ? "bg-[#d4a84b] text-black" 
                        : "bg-black/30 text-gray-400 hover:bg-black/50 border border-[rgba(212,168,75,0.2)]"
                    }`}
                    data-testid="report-type-attendance"
                  >
                    <Clock className="w-4 h-4" />
                    Check-In / Check-Out
                  </button>
                  <button
                    onClick={() => setReportType("breaks")}
                    className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      reportType === "breaks" 
                        ? "bg-[#d4a84b] text-black" 
                        : "bg-black/30 text-gray-400 hover:bg-black/50 border border-[rgba(212,168,75,0.2)]"
                    }`}
                    data-testid="report-type-breaks"
                  >
                    <Coffee className="w-4 h-4" />
                    Breaks
                  </button>
                </div>
              </div>
            </div>

            {/* Summary Section */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg gold-text font-bold">Summary Report</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSummaryPeriod("weekly")}
                    className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors ${
                      summaryPeriod === "weekly" 
                        ? "bg-[#d4a84b] text-black" 
                        : "bg-black/30 text-gray-400 hover:bg-black/50"
                    }`}
                    data-testid="summary-weekly"
                  >
                    Weekly
                  </button>
                  <button
                    onClick={() => setSummaryPeriod("monthly")}
                    className={`px-4 py-1 rounded-lg text-sm font-medium transition-colors ${
                      summaryPeriod === "monthly" 
                        ? "bg-[#d4a84b] text-black" 
                        : "bg-black/30 text-gray-400 hover:bg-black/50"
                    }`}
                    data-testid="summary-monthly"
                  >
                    Monthly
                  </button>
                </div>
              </div>
              
              {summary && (
                <div className="overflow-x-auto">
                  <table className="w-full attendance-table">
                    <thead>
                      <tr>
                        <th className="py-3 px-4 text-left text-[#d4a84b]">Staff</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Days Worked</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Total Work Hours</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Total Break Hours</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Avg Hours/Day</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.staff_summaries?.map(s => (
                        <tr key={s.staff_id}>
                          <td className="py-3 px-4 text-white font-medium">{s.staff_name}</td>
                          <td className="py-3 px-4 text-center text-gray-300">{s.total_days}</td>
                          <td className="py-3 px-4 text-center text-gray-300">{s.total_work_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-center text-gray-300">{s.total_break_hours.toFixed(1)}h</td>
                          <td className="py-3 px-4 text-center text-gray-300">{s.avg_work_hours_per_day}h</td>
                        </tr>
                      ))}
                      {(!summary.staff_summaries || summary.staff_summaries.length === 0) && (
                        <tr>
                          <td colSpan="5" className="py-8 text-center text-gray-500">
                            No data available for this period
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Detailed Records - Attendance */}
            {reportType === "attendance" && (
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg gold-text font-bold">Detailed Attendance Records</h3>
                  <button
                    onClick={downloadCSV}
                    disabled={reports.length === 0}
                    className="px-4 py-2 bg-green-600/30 hover:bg-green-600/50 border border-green-500/30 rounded-lg text-green-400 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
                    data-testid="download-csv-btn"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full attendance-table">
                    <thead>
                      <tr>
                        <th className="py-3 px-4 text-left text-[#d4a84b]">Staff</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Check In</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Check Out</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Breaks</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Total Break</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Total Work</th>
                        {user?.role === 'admin' && <th className="py-3 px-4 text-center text-[#d4a84b]">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map((r, idx) => (
                        <React.Fragment key={r.id}>
                          <tr className={idx % 2 === 0 ? 'bg-[rgba(212,168,75,0.03)]' : 'bg-transparent'}>
                            <td className="py-3 px-4 text-white font-medium">{r.staff_name}</td>
                            <td className="py-3 px-4 text-center text-cyan-400">{formatDateTime(r.checkin)}</td>
                            <td className="py-3 px-4 text-center text-red-400">{r.checkout ? formatDateTime(r.checkout) : '—'}</td>
                            <td className="py-3 px-4 text-center">
                              {r.breaks && r.breaks.length > 0 ? (
                                <button
                                  onClick={() => setExpandedBreaks(prev => ({...prev, [r.id]: !prev[r.id]}))}
                                  className={`cursor-pointer hover:underline ${!r.checkout && r.breaks.some(b => !b.end) ? 'text-cyan-400' : 'text-amber-400'}`}
                                  title="Click to see break details"
                                >
                                  {r.breaks.length}
                                  {!r.checkout && r.breaks.some(b => !b.end) && <span className="text-cyan-400 ml-1">(active)</span>}
                                  <span className="border-b border-dotted border-current ml-1">...</span>
                                </button>
                              ) : (
                                <span className="text-gray-500">0</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center text-amber-400">{formatDuration(r.total_break_minutes)}</td>
                            <td className="py-3 px-4 text-center text-green-400">{formatDuration(r.total_work_minutes)}</td>
                            {user?.role === 'admin' && (
                              <td className="py-3 px-4">
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleEditAttendance(r)}
                                    className="p-2 bg-blue-600/30 hover:bg-blue-600/50 border border-blue-500/30 rounded-lg text-blue-400 transition-colors"
                                    title="Edit Record"
                                    data-testid={`edit-attendance-${r.id}`}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteAttendance(r.id)}
                                    className="p-2 bg-red-600/30 hover:bg-red-600/50 border border-red-500/30 rounded-lg text-red-400 transition-colors"
                                    title="Delete Record"
                                    data-testid={`delete-attendance-${r.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                          {/* Expanded Break Details */}
                          {expandedBreaks[r.id] && r.breaks && r.breaks.length > 0 && (
                            <tr className="bg-[rgba(212,168,75,0.08)]">
                              <td colSpan={user?.role === 'admin' ? 7 : 6} className="py-3 px-4">
                                <div className="ml-4 border-l-2 border-amber-500/50 pl-4">
                                  <p className="text-xs text-amber-400 uppercase tracking-wider mb-3 font-semibold">Break Details</p>
                                  <div className="space-y-2">
                                    {r.breaks.map((b, bIdx) => {
                                      const breakStart = b.start ? new Date(b.start) : null;
                                      const breakEnd = b.end ? new Date(b.end) : null;
                                      const durationMs = breakStart && breakEnd ? (breakEnd - breakStart) : 0;
                                      const durationHrs = Math.floor(durationMs / 3600000);
                                      const durationMins = Math.floor((durationMs % 3600000) / 60000);
                                      const durationSecs = Math.floor((durationMs % 60000) / 1000);
                                      
                                      const formatBreakTime = (date) => {
                                        if (!date) return '—';
                                        return date.toLocaleTimeString('pl-PL', {
                                          hour: '2-digit', 
                                          minute: '2-digit',
                                          second: '2-digit',
                                          timeZone: 'Europe/Warsaw'
                                        });
                                      };
                                      
                                      return (
                                        <div key={bIdx} className="flex items-center gap-3 text-sm bg-black/20 rounded-lg px-3 py-2">
                                          <span className="text-amber-500 font-bold min-w-[70px]">Break #{bIdx + 1}</span>
                                          <div className="flex items-center gap-2">
                                            <span className="text-gray-400 text-xs uppercase">Start:</span>
                                            <span className="text-cyan-400 font-mono">{formatBreakTime(breakStart)}</span>
                                          </div>
                                          <span className="text-gray-600">→</span>
                                          <div className="flex items-center gap-2">
                                            <span className="text-gray-400 text-xs uppercase">End:</span>
                                            <span className={`font-mono ${b.end ? 'text-green-400' : 'text-amber-400 animate-pulse'}`}>
                                              {b.end ? formatBreakTime(breakEnd) : '(active)'}
                                            </span>
                                          </div>
                                          {b.end && (
                                            <div className="flex items-center gap-2 ml-auto">
                                              <span className="text-gray-400 text-xs uppercase">Duration:</span>
                                              <span className="text-amber-400 font-mono font-semibold">
                                                {durationHrs > 0 ? `${durationHrs}h ` : ''}{durationMins}m {durationSecs}s
                                              </span>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                      {reports.length === 0 && (
                        <tr>
                          <td colSpan={user?.role === 'admin' ? 7 : 6} className="py-8 text-center text-gray-500">
                            No attendance records found for the selected filters
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Detailed Records - Breaks */}
            {reportType === "breaks" && (
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg gold-text font-bold flex items-center gap-2">
                    <Coffee className="w-5 h-5" />
                    Detailed Break Records
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full attendance-table">
                    <thead>
                      <tr>
                        <th className="py-3 px-4 text-left text-[#d4a84b]">Staff</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Date</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Break #</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Break Start (Date & Time)</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Break End (Date & Time)</th>
                        <th className="py-3 px-4 text-center text-[#d4a84b]">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakReports.map((b, idx) => (
                        <tr key={`${b.attendance_id}-${b.break_number}-${idx}`}>
                          <td className="py-3 px-4 text-white font-medium">{b.staff_name}</td>
                          <td className="py-3 px-4 text-center text-gray-300">{formatDate(b.date)}</td>
                          <td className="py-3 px-4 text-center text-amber-400">#{b.break_number}</td>
                          <td className="py-3 px-4 text-center text-amber-400">{formatDateTime(b.break_start)}</td>
                          <td className="py-3 px-4 text-center text-green-400">{formatDateTime(b.break_end)}</td>
                          <td className="py-3 px-4 text-center text-gray-300">{formatDuration(b.duration_minutes)}</td>
                        </tr>
                      ))}
                      {breakReports.length === 0 && (
                        <tr>
                          <td colSpan="6" className="py-8 text-center text-gray-500">
                            No break records found for the selected filters
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && user?.role === 'admin' && (
          <div className="space-y-6 animate-fade-in" data-testid="users-panel">
            {/* WhatsApp Notifications Card */}
            <div className="glass-card rounded-2xl p-6" data-testid="whatsapp-panel">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl gold-text font-bold flex items-center gap-2">
                    <MessageCircle className="w-6 h-6" />
                    WhatsApp Notifications
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Vardiya bitiminde geç giriş özetlerini yöneticilere WhatsApp ile otomatik gönderir.
                  </p>
                </div>
                <button
                  onClick={handleToggleEnabled}
                  className={`px-4 py-2 rounded-lg font-medium border transition-all ${
                    waSettings.enabled
                      ? "bg-green-600/20 border-green-500/40 text-green-400"
                      : "bg-gray-700/40 border-gray-600 text-gray-400"
                  }`}
                  data-testid="wa-toggle-enabled"
                >
                  {waSettings.enabled ? "Aktif" : "Pasif"}
                </button>
              </div>

              {!waSettings.configured && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  Twilio yapılandırması eksik. Backend .env dosyasındaki TWILIO_* değerlerini kontrol edin.
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recipient Numbers */}
                <div>
                  <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Yönetici Numaraları
                  </h3>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={waNewNumber}
                      onChange={(e) => setWaNewNumber(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddRecipient(); } }}
                      placeholder="+90 555 444 33 22"
                      className="flex-1 bg-black/40 border border-[rgba(212,168,75,0.2)] rounded-lg px-3 py-2 text-white font-mono focus:border-amber-400 outline-none"
                      data-testid="wa-new-number-input"
                    />
                    <button
                      onClick={handleAddRecipient}
                      className="gold-button px-4 py-2 rounded-lg font-medium flex items-center gap-1"
                      data-testid="wa-add-number-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Ekle
                    </button>
                  </div>
                  <div className="space-y-2">
                    {waSettings.recipients.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">Henüz numara eklenmedi.</p>
                    ) : (
                      waSettings.recipients.map((num) => (
                        <div key={num} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2 border border-[rgba(212,168,75,0.1)]">
                          <span className="font-mono text-white">{num}</span>
                          <button
                            onClick={() => handleRemoveRecipient(num)}
                            className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                            data-testid={`wa-remove-${num}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    E.164 formatı: <span className="font-mono text-gray-400">+ülkekodu rakamlar</span> (boşluksuz). Sandbox test için her numaranın önceden WhatsApp&apos;tan <span className="font-mono text-amber-400">+1 415 523 8886</span>&apos;ya join kodu göndermesi gerekir.
                  </p>
                </div>

                {/* Shift Send Controls */}
                <div>
                  <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Vardiya Özetleri
                  </h3>
                  <div className="space-y-2">
                    {[
                      { id: "morning", label: "Sabah", official: "07:45", auto: "11:01" },
                      { id: "evening", label: "Akşam", official: "15:45", auto: "19:01" },
                      { id: "night",   label: "Gece",  official: "23:45", auto: "04:01 (ertesi gün)" },
                    ].map(s => (
                      <div key={s.id} className="bg-black/30 rounded-lg p-3 border border-[rgba(212,168,75,0.1)]">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <div className="text-white font-medium">{s.label} Vardiyası</div>
                            <div className="text-xs text-gray-500">
                              Resmi: <span className="text-cyan-400">{s.official}</span> · Otomatik gönderim: <span className="text-amber-400">{s.auto}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => fetchPreview(s.id)}
                              className="px-3 py-1.5 rounded border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 text-sm hover:bg-cyan-500/20 flex items-center gap-1"
                              data-testid={`wa-preview-${s.id}`}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              Önizle
                            </button>
                            <button
                              onClick={() => handleSendNow(s.id)}
                              disabled={waSending[s.id] || !waSettings.enabled}
                              className="px-3 py-1.5 rounded gold-button text-sm flex items-center gap-1 disabled:opacity-50"
                              data-testid={`wa-send-${s.id}`}
                            >
                              <Send className="w-3.5 h-3.5" />
                              {waSending[s.id] ? "Gönderiliyor..." : "Şimdi Gönder"}
                            </button>
                          </div>
                        </div>
                        {waPreview[s.id] && (
                          <pre className="mt-2 text-xs text-gray-300 bg-black/40 rounded p-2 whitespace-pre-wrap font-mono border border-[rgba(212,168,75,0.1)]" data-testid={`wa-preview-text-${s.id}`}>
                            {waPreview[s.id].message}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* User Management Card */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl gold-text font-bold">User Management</h2>
                  <p className="text-gray-400 text-sm mt-1">Create accounts and manage roles & passwords.</p>
                </div>
                <button
                  onClick={() => setUserModalOpen(true)}
                  className="gold-button px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                  data-testid="add-user-btn"
                >
                  <Plus className="w-5 h-5" />
                  Add User
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full attendance-table">
                <thead>
                  <tr>
                    <th className="py-4 px-6 text-left text-[#d4a84b]">Username</th>
                    <th className="py-4 px-6 text-center text-[#d4a84b]">Role</th>
                    <th className="py-4 px-6 text-center text-[#d4a84b]">Created</th>
                    <th className="py-4 px-6 text-center text-[#d4a84b]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {usersList.map((u, idx) => (
                    <tr key={u.id} className="animate-fade-in" style={{ animationDelay: `${idx * 0.04}s` }}>
                      <td className="py-4 px-6 text-white font-medium flex items-center gap-2">
                        {u.role === 'admin' ? <ShieldCheck className="w-4 h-4 text-amber-400" /> : <User className="w-4 h-4 text-gray-400" />}
                        {u.username}
                        {u.id === user?.admin_id && <span className="text-xs text-amber-400 ml-1">(you)</span>}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <select
                          value={u.role}
                          disabled={u.id === user?.admin_id}
                          onChange={(e) => handleChangeRole(u.id, e.target.value)}
                          className={`bg-black/40 border border-[rgba(212,168,75,0.3)] rounded px-3 py-1 text-sm ${
                            u.role === 'admin' ? 'text-amber-400' : 'text-gray-300'
                          } ${u.id === user?.admin_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                          data-testid={`role-select-${u.username}`}
                        >
                          <option value="admin">Admin</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </td>
                      <td className="py-4 px-6 text-center text-gray-400 text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' }) : '—'}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => { setEditingUser(u); setPasswordModalOpen(true); }}
                            className="p-2 bg-amber-600/30 hover:bg-amber-600/50 border border-amber-500/30 rounded-lg text-amber-400 transition-colors"
                            title="Change password"
                            data-testid={`change-password-${u.username}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u.id)}
                            disabled={u.id === user?.admin_id}
                            className={`p-2 border rounded-lg transition-colors ${
                              u.id === user?.admin_id
                                ? 'bg-gray-600/20 border-gray-600/30 text-gray-600 cursor-not-allowed'
                                : 'bg-red-600/30 hover:bg-red-600/50 border-red-500/30 text-red-400'
                            }`}
                            title={u.id === user?.admin_id ? "You can't delete your own account" : "Delete user"}
                            data-testid={`delete-user-${u.username}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {usersList.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-12 text-center text-gray-500">
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            </div>
          </div>
        )}
      </main>

      {/* Staff Modal */}
      <StaffModal
        isOpen={staffModalOpen}
        onClose={() => { setStaffModalOpen(false); setEditingStaff(null); }}
        staff={editingStaff}
        onSave={handleSaveStaff}
      />

      {/* Attendance Edit Modal */}
      <AttendanceEditModal
        isOpen={attendanceModalOpen}
        onClose={() => { setAttendanceModalOpen(false); setEditingAttendance(null); }}
        record={editingAttendance}
        onSave={handleSaveAttendance}
      />

      {/* User Create Modal */}
      <UserCreateModal
        isOpen={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSave={handleCreateUser}
      />

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={passwordModalOpen}
        onClose={() => { setPasswordModalOpen(false); setEditingUser(null); }}
        user={editingUser}
        onSave={handleChangePassword}
      />
    </div>
  );
};

// ==================== MAIN APP ====================
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (token) {
        try {
          const res = await axios.get(`${API}/auth/me`, authHeader());
          setUser(res.data);
        } catch {
          clearToken();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const handleLogin = (data) => {
    setUser({ admin_id: data.admin_id, email: data.email, role: data.role });
  };

  const handleLogout = () => {
    clearToken();
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse gold-text text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      {user ? (
        <Dashboard user={user} onLogout={handleLogout} />
      ) : (
        <LoginPage onLogin={handleLogin} />
      )}
    </div>
  );
}

export default App;
